import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO, { createContactPageSchema, createWebPageSchema, createBreadcrumbSchema, createOrganizationSchema, createLocalBusinessSchema } from '../../components/feature/SEO';
import WhatsAppButton from '../../components/base/WhatsAppButton';
import { useTranslation } from 'react-i18next';
import { getImageUrl } from '../../utils/siteSettings';

const CONTACT_HERO_DEFAULT = 'https://public.readdy.ai/ai/img_res/f4c903ecea8d6d8137cc746fded70e1e.jpg';

const contactInfo = [
  { icon: 'ri-mail-send-line', labelKey: 'contact.heroLabel', value: 'Info@unqcode.com', href: 'mailto:Info@unqcode.com' },
  { icon: 'ri-phone-line', labelKey: 'footer.phoneLabel', value: '+966 552 099 231', href: 'tel:+966552099231' },
  { icon: 'ri-map-pin-2-line', labelKey: 'about.val5Title', value: 'Riyadh, Saudi Arabia', href: '#' },
  { icon: 'ri-time-line', labelKey: 'cta.trust2', value: 'Sun–Thu: 9AM – 6PM', href: '#' },
];

const socials = [
  { icon: 'ri-twitter-x-line', label: 'Twitter', href: '#' },
  { icon: 'ri-instagram-line', label: 'Instagram', href: '#' },
  { icon: 'ri-linkedin-line', label: 'LinkedIn', href: '#' },
  { icon: 'ri-youtube-line', label: 'YouTube', href: '#' },
];

const Contact = () => {
  const { t } = useTranslation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://unqcode.com';
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [chars, setChars] = useState(0);

  const services = [
    t('svc.item1Title'), t('svc.item2Title'), t('svc.item3Title'),
    t('svc.item4Title'), t('svc.item5Title'), t('svc.item6Title'),
    t('nav.revenueSharing'), 'Other',
  ];

  const contactDetails = [
    { icon: 'ri-mail-send-line', labelKey: 'footer.emailLabel', value: 'Info@unqcode.com', href: 'mailto:Info@unqcode.com', valueKey: '' },
    { icon: 'ri-phone-line', labelKey: 'footer.phoneLabel', value: '+966 552 099 231', href: 'tel:+966552099231', valueKey: '' },
    { icon: 'ri-map-pin-2-line', labelKey: 'about.heroLabel', value: '', href: '#', valueKey: 'contact.locationValue' },
    { icon: 'ri-time-line', labelKey: 'cta.trust2', value: '', href: '#', valueKey: 'contact.hoursValue' },
  ];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (chars > 500) return;
    setStatus('sending');
    const form = e.currentTarget;
    const data = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (typeof value === 'string') data.append(key, value);
    });
    try {
      const res = await fetch('https://readdy.ai/api/form/d71gup8hb0rqicn68hhg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) { setStatus('sent'); form.reset(); setChars(0); }
      else setStatus('error');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEO
        title={t('seo.contactTitle')}
        description={t('seo.contactDesc')}
        keywords="contact Unique Code, contact us, web development inquiry, digital agency contact, Riyadh tech company, تواصل معنا, الاتصال بنا"
        canonical={`${siteUrl}/contact`}
        ogType="website"
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            createWebPageSchema(
              t('seo.contactTitle', 'Contact Us - Get in Touch | Unique Code - الكود المميز'),
              t('seo.contactDesc', 'Contact Unique Code for web development, mobile apps, AI solutions, and digital marketing services. Riyadh, Saudi Arabia.'),
              '/contact'
            ),
            createOrganizationSchema(),
            createLocalBusinessSchema(),
            createContactPageSchema(),
            createBreadcrumbSchema([
              { name: 'Home', url: '/' },
              { name: 'Contact Us', url: '/contact' },
            ]),
          ],
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gray-900 overflow-hidden">
        <div className="absolute inset-0">
          <img src={getImageUrl('hero-contact', CONTACT_HERO_DEFAULT)} alt="" className="w-full h-full object-cover object-top opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 to-gray-900"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-brand-red font-bold text-sm uppercase tracking-widest mb-4">{t('contact.heroLabel')}</p>
          <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6">
            {t('contact.heroTitle1')} <span className="text-brand-red">{t('contact.heroTitle2')}</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-xl mx-auto">{t('contact.heroDesc')}</p>
        </div>
      </section>

      {/* Main content */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-5 gap-16">
            {/* Left: Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <p className="text-brand-red font-semibold text-sm uppercase tracking-widest mb-3">{t('contact.infoLabel')}</p>
                <h2 className="text-3xl font-black text-gray-900 mb-6">{t('contact.infoTitle')}</h2>
                <p className="text-gray-500 text-sm leading-relaxed">{t('contact.infoDesc')}</p>
              </div>
              <div className="space-y-4">
                {contactDetails.map((item) => (
                  <a key={item.labelKey} href={item.href} rel="nofollow" className="flex items-center gap-4 p-5 bg-white rounded-2xl border border-gray-100 hover:border-brand-red/30 transition-all duration-200 group cursor-pointer">
                    <div className="w-12 h-12 flex items-center justify-center bg-brand-red/10 group-hover:bg-brand-red rounded-xl transition-colors flex-shrink-0">
                      <i className={`${item.icon} text-brand-red group-hover:text-white text-xl transition-colors`}></i>
                    </div>
                    <div>
                      <div className="text-gray-400 text-xs mb-0.5 font-medium">{t(item.labelKey)}</div>
                      <div className="text-gray-900 font-bold text-sm group-hover:text-brand-red transition-colors">{item.valueKey ? t(item.valueKey) : item.value}</div>
                    </div>
                  </a>
                ))}
              </div>
              <div>
                <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mb-4">{t('contact.followTitle')}</p>
                <div className="flex gap-3">
                  {socials.map((s) => (
                    <a key={s.label} href={s.href} rel="nofollow" aria-label={s.label} className="w-11 h-11 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-brand-red text-gray-500 hover:text-white transition-all duration-200 cursor-pointer">
                      <i className={`${s.icon} text-base`}></i>
                    </a>
                  ))}
                </div>
              </div>

            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              {status === 'sent' ? (
                <div className="text-center py-20 bg-gray-50 rounded-3xl border border-gray-100 h-full flex flex-col items-center justify-center">
                  <div className="w-24 h-24 flex items-center justify-center bg-brand-red/10 border-2 border-brand-red rounded-full mb-6">
                    <i className="ri-check-double-line text-brand-red text-4xl"></i>
                  </div>
                  <h3 className="text-2xl font-black text-gray-900 mb-3">{t('contact.successTitle')}</h3>
                  <p className="text-gray-500 max-w-xs text-base leading-relaxed">{t('contact.successDesc')}</p>
                  <button onClick={() => setStatus('idle')} className="mt-8 px-7 py-3.5 bg-brand-red text-white font-bold rounded-xl hover:bg-brand-red-dark transition-colors cursor-pointer whitespace-nowrap">{t('contact.successBtn')}</button>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-3xl border border-gray-100 p-8 space-y-5">
                  <h3 className="text-xl font-black text-gray-900 mb-2">{t('contact.formTitle')}</h3>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-gray-600 text-sm font-semibold mb-2">{t('contact.nameLabel')} *</label>
                      <input name="full_name" required type="text" placeholder={t('contact.namePlaceholder')} className="w-full border border-gray-200 text-gray-900 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-red transition-colors placeholder-gray-400" />
                    </div>
                    <div>
                      <label className="block text-gray-600 text-sm font-semibold mb-2">{t('contact.emailLabel2')} *</label>
                      <input name="email" required type="email" placeholder={t('contact.emailPlaceholder')} className="w-full border border-gray-200 text-gray-900 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-red transition-colors placeholder-gray-400" />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-gray-600 text-sm font-semibold mb-2">{t('contact.phoneLabel2')}</label>
                      <input name="phone" type="tel" placeholder={t('contact.phonePlaceholder')} className="w-full border border-gray-200 text-gray-900 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-red transition-colors placeholder-gray-400" />
                    </div>
                    <div>
                      <label className="block text-gray-600 text-sm font-semibold mb-2">{t('contact.companyLabel')}</label>
                      <input name="company" type="text" placeholder={t('contact.companyPlaceholder')} className="w-full border border-gray-200 text-gray-900 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-red transition-colors placeholder-gray-400" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-600 text-sm font-semibold mb-2">{t('contact.serviceLabel')} *</label>
                    <select name="service" required className="w-full border border-gray-200 text-gray-900 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-red transition-colors cursor-pointer">
                      <option value="">{t('contact.serviceDefault')}</option>
                      {services.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-600 text-sm font-semibold mb-2">{t('contact.budgetLabel')}</label>
                    <select name="budget" className="w-full border border-gray-200 text-gray-900 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-red transition-colors cursor-pointer">
                      <option value="">{t('contact.budgetDefault')}</option>
                      <option value="Under $5,000">Under $5,000</option>
                      <option value="$5,000 – $15,000">$5,000 – $15,000</option>
                      <option value="$15,000 – $50,000">$15,000 – $50,000</option>
                      <option value="$50,000+">$50,000+</option>
                      <option value="Revenue Sharing Model">Revenue Sharing Model</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-600 text-sm font-semibold mb-2">
                      {t('contact.messageLabel')} *
                      <span className={`ml-2 font-normal text-xs ${chars > 500 ? 'text-red-500' : 'text-gray-400'}`}>{chars}/500</span>
                    </label>
                    <textarea name="message" required rows={5} maxLength={500} onChange={(e) => setChars(e.target.value.length)} placeholder={t('contact.messagePlaceholder')} className="w-full border border-gray-200 text-gray-900 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-brand-red transition-colors placeholder-gray-400 resize-none"></textarea>
                  </div>
                  {status === 'error' && <p className="text-red-500 text-sm text-center">{t('contact.errorText')}</p>}
                  <button type="submit" disabled={status === 'sending' || chars > 500} className="w-full py-4 bg-brand-red hover:bg-brand-red-dark text-white font-bold rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                    {status === 'sending' ? <><i className="ri-loader-4-line animate-spin"></i> {t('contact.sendingText')}</> : <><i className="ri-send-plane-fill"></i> {t('contact.submitBtn')}</>}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Contact;
